import React, {useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {apiErrorMessage} from '../api/abfApi';
import {DateField} from '../../../components/shared/DateField';
import {Button, INK} from '../onboarding/review/reviewStyles';
import {SmallButton} from '../onboarding/review/contractReviewStyles';
import {ffApi, type MappingSuggestion} from './forwardFlowApi';
import {AmountInput, amountOf, Fields, GatedAction, Grid, Notice, Row, Scroller, Section, useFeedback, useViewer} from './ForwardFlowShared';
import {count, loanFieldLabel, money} from './forwardFlowWords';

const Lanes = styled.div`
  display: flex; gap: 5px; padding: 5px; margin: 0 0 16px; border: 1px solid ${INK.line}; border-radius: 10px; background: ${INK.raised}; width: fit-content;
  button { min-height: 36px; padding: 7px 14px; border: 0; border-radius: 7px; background: transparent; color: ${INK.muted};
    cursor: pointer; font: inherit; font-size: 13px; font-weight: 650; }
  button[aria-selected='true'] { color: ${INK.strong}; background: #19334a; box-shadow: inset 0 -2px ${INK.accent}; }
`;
const Sheet = styled(Grid)`
  td { padding: 4px; } td input { min-width: 96px; min-height: 34px !important; }
  td:first-child { color: ${INK.faint}; padding: 4px 8px; vertical-align: middle; }
`;

/** What the person types for each loan; the agreement supplies the rest. */
const ROW_FIELDS: Array<{key: string; type: 'text' | 'date' | 'amount' | 'number'; required: boolean; width?: number}> = [
  {key: 'sourceLoanId', type: 'text', required: true, width: 140}, {key: 'obligorToken', type: 'text', required: true, width: 130},
  {key: 'stateCode', type: 'text', required: true, width: 70}, {key: 'creditScore', type: 'number', required: true, width: 90},
  {key: 'originalPrincipal', type: 'amount', required: true, width: 130}, {key: 'cutoffUpb', type: 'amount', required: true, width: 130},
  {key: 'couponRatePct', type: 'number', required: true, width: 90}, {key: 'purchasedInterest', type: 'amount', required: false, width: 120},
  {key: 'originationDate', type: 'date', required: true, width: 125}, {key: 'maturityDate', type: 'date', required: true, width: 125},
  {key: 'nextDueDate', type: 'date', required: true, width: 125}, {key: 'productSubtype', type: 'text', required: true, width: 130},
];
const ISO = /^\d{4}-\d{2}-\d{2}$/;
const asIso = (text: string): string => {
  const t = text.trim();
  if (ISO.test(t)) return t;
  const us = t.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (us) return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
  const d = new Date(t);
  return t && !Number.isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : t;
};

/**
 * Receive a seller offer: bring in the seller's file, or type its loans. Either way the
 * offer is kept as received, checked against its stated totals, and nothing is bought.
 */
export function ForwardFlowOfferIntake({programId, businessDate, pricePer100, sellerReference: fixedReference, onReceived, onCancel}: {
  programId: number; businessDate: string; pricePer100?: string | number; sellerReference?: string;
  onReceived: (deliveryId: number) => Promise<unknown> | void; onCancel?: () => void;
}) {
  const viewer = useViewer();
  const notify = useFeedback();
  const [lane, setLane] = useState<'file' | 'rows'>('file');
  const [reference, setReference] = useState(fixedReference ?? '');
  const [cutoff, setCutoff] = useState('');
  const [statedCount, setStatedCount] = useState('');
  const [statedUpb, setStatedUpb] = useState('');
  const [busy, setBusy] = useState(false);

  // File lane
  const picker = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [reading, setReading] = useState(false);
  const [suggestion, setSuggestion] = useState<MappingSuggestion | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const keys = suggestion ? [...Object.keys(suggestion.proposedMapping), ...suggestion.unmappedFields] : [];
  const unmapped = keys.filter(k => !mapping[k]);
  const chosen = Object.values(mapping).filter(Boolean);
  const duplicated = new Set(chosen).size !== chosen.length;

  // Rows lane
  const [rows, setRows] = useState<Array<Record<string, string>>>([{}]);
  const [paste, setPaste] = useState('');
  const filled = useMemo(() => rows.filter(r => Object.values(r).some(v => String(v ?? '').trim() !== '')), [rows]);
  const rowsTotal = filled.reduce((sum, r) => sum + (amountOf(r.cutoffUpb) ?? 0), 0);
  const rowProblems = useMemo(() => {
    const out: string[] = [];
    filled.forEach((r, i) => {
      const missing = ROW_FIELDS.filter(f => f.required && !String(r[f.key] ?? '').trim()).map(f => loanFieldLabel(f.key).toLowerCase());
      if (missing.length) out.push(`Loan ${i + 1}: ${missing.join(', ')} missing`);
      ROW_FIELDS.filter(f => f.type === 'date' && r[f.key] && !ISO.test(asIso(r[f.key]))).forEach(f => out.push(`Loan ${i + 1}: ${loanFieldLabel(f.key).toLowerCase()} is not a date`));
      ROW_FIELDS.filter(f => f.type === 'amount' && r[f.key] && amountOf(r[f.key]) === null).forEach(f => out.push(`Loan ${i + 1}: ${loanFieldLabel(f.key).toLowerCase()} is not an amount`));
    });
    return out;
  }, [filled]);

  const readFile = async (chosenFile: File | null) => {
    setFile(chosenFile); setSuggestion(null); setMapping({});
    if (!chosenFile) return;
    setReading(true);
    try {
      const s = await ffApi.suggestMapping(programId, chosenFile);
      setSuggestion(s); setMapping(s.proposedMapping);
    } catch (e) { notify({tone: 'error', message: apiErrorMessage(e, `${chosenFile.name} could not be read. Use a CSV or XLSX file with a header row.`)}); }
    finally { setReading(false); }
  };

  const applyPaste = () => {
    const lines = paste.trim().split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) { notify({tone: 'error', message: 'Paste the header row and at least one loan.'}); return; }
    const split = (l: string) => l.split(l.includes('\t') ? '\t' : ',').map(x => x.trim());
    const norm = (h: string) => h.toLowerCase().replace(/[^a-z0-9]+/g, '');
    const header = split(lines[0]).map(norm);
    const keyFor = (h: string) => ROW_FIELDS.find(f => norm(f.key) === h || norm(loanFieldLabel(f.key)) === h)?.key;
    const matched = header.filter(keyFor).length;
    if (!matched) { notify({tone: 'error', message: `No column was recognised. Use these headings: ${ROW_FIELDS.map(f => loanFieldLabel(f.key)).join(', ')}.`}); return; }
    setRows(lines.slice(1).map(l => {
      const cells = split(l); const r: Record<string, string> = {};
      header.forEach((h, i) => { const k = keyFor(h); if (k) r[k] = cells[i] ?? ''; });
      return r;
    }));
    setPaste('');
    notify({tone: 'info', message: `${count(lines.length - 1, 'loan')} filled in from ${matched} recognised column${matched === 1 ? '' : 's'}. Check them before receiving.`});
  };

  const shared = !reference.trim() ? "Enter the seller's reference for this offer."
    : !cutoff ? 'Enter the cutoff date the balances are stated as of.' : null;
  const fileReason = !viewer.maker ? 'An operations or portfolio user receives the offer.' : shared
    ?? (!file ? "Choose the seller's file." : reading ? 'Reading the file…' : !suggestion ? 'The file could not be read.'
      : unmapped.length ? `${count(unmapped.length, 'column')} still to match below.`
      : duplicated ? 'One seller column is matched to two loan fields.'
      : !statedCount || !(Number(statedCount) > 0) ? "Enter the seller's stated loan count."
      : !((amountOf(statedUpb) ?? 0) > 0) ? "Enter the seller's stated total balance." : null);
  const rowsReason = !viewer.maker ? 'An operations or portfolio user receives the offer.' : shared
    ?? (!filled.length ? 'Enter at least one loan.' : rowProblems.length ? rowProblems[0] : null);

  const receiveFile = async () => {
    setBusy(true);
    try {
      const saved = await ffApi.createMapping(programId, `${file!.name} · ${new Date().toISOString().slice(0, 10)}`, mapping);
      const receipt = await ffApi.uploadDelivery(programId, {sellerReference: reference.trim(), deliveryType: 'PURCHASE_OFFER', cutoffDate: cutoff,
        statedCount: Number(statedCount), statedUpb: amountOf(statedUpb)!, mappingId: Number((saved as any).mappingId), file: file!});
      notify({tone: receipt.controlTotalsMatch ? 'success' : 'info', message: `${reference.trim()} received: ${count(receipt.normalizedCount, 'loan')}, ${money(receipt.normalizedUpb)}. `
        + (receipt.controlTotalsMatch ? "It matches the seller's stated totals." : "It does not match the seller's stated totals; review the differences.")
        + ' Nothing has been bought.'});
      await onReceived(receipt.deliveryId);
    } catch (e) { notify({tone: 'error', message: apiErrorMessage(e, 'The offer could not be received.')}); }
    finally { setBusy(false); }
  };

  const receiveRows = async () => {
    setBusy(true);
    try {
      const body = filled.map(r => {
        const out: Record<string, string | number> = {currency: 'USD', rateType: 'FIXED', paymentFrequency: 'MONTHLY'};
        if (pricePer100 !== undefined && pricePer100 !== '') out.pricePer100 = Number(pricePer100);
        ROW_FIELDS.forEach(f => {
          const raw = String(r[f.key] ?? '').trim();
          if (raw === '') { if (f.key === 'purchasedInterest') out[f.key] = 0; return; }
          out[f.key] = f.type === 'amount' ? amountOf(raw)! : f.type === 'number' ? Number(raw) : f.type === 'date' ? asIso(raw)
            : f.key === 'stateCode' ? raw.toUpperCase() : raw;
        });
        return out;
      });
      const receipt: any = await ffApi.createStructuredDelivery(programId, {sellerReference: reference.trim(), deliveryType: 'PURCHASE_OFFER', cutoffDate: cutoff,
        statedCount: statedCount ? Number(statedCount) : filled.length, statedUpb: amountOf(statedUpb) ?? rowsTotal, rows: body});
      notify({tone: 'success', message: `${reference.trim()} received: ${count(filled.length, 'loan')}, ${money(rowsTotal)}. Nothing has been bought.`});
      await onReceived(receipt.deliveryId);
    } catch (e) { notify({tone: 'error', message: apiErrorMessage(e, 'The offer could not be received.')}); }
    finally { setBusy(false); }
  };

  return <Section>
    <header><div><h2>{fixedReference ? `Receive a corrected file for ${fixedReference}` : 'Receive a seller offer'}</h2>
      <p>{fixedReference ? 'The corrected file becomes the next revision of this offer. The earlier one is kept as received.'
        : 'The offer is kept exactly as received and checked against the seller\'s stated totals. Receiving it buys nothing.'}</p></div>
      {onCancel && <SmallButton type="button" onClick={onCancel}>Cancel</SmallButton>}</header>

    <Lanes role="tablist" aria-label="How the offer arrives">
      <button role="tab" aria-selected={lane === 'file'} onClick={() => setLane('file')}>Seller's file (CSV or XLSX)</button>
      <button role="tab" aria-selected={lane === 'rows'} onClick={() => setLane('rows')}>Type or paste the loans</button>
    </Lanes>

    <Fields $cols={4}>
      <label>Seller's reference<input value={reference} disabled={!!fixedReference} maxLength={120} onChange={e => setReference(e.target.value)} placeholder="e.g. GRANITE-2026-W48" /></label>
      <label>Cutoff date<DateField value={cutoff} onChange={setCutoff} today={businessDate} max={businessDate} maxLabel="the business date" aria-label="Cutoff date" /></label>
      <label>Seller's stated loan count<input inputMode="numeric" value={statedCount} onChange={e => setStatedCount(e.target.value.replace(/[^\d]/g, ''))}
        placeholder={lane === 'rows' && filled.length ? String(filled.length) : ''} />
        {lane === 'rows' && <small>Leave empty to use the number of loans entered.</small>}</label>
      <label>Seller's stated total balance<AmountInput value={statedUpb} onChange={setStatedUpb} aria-label="Seller's stated total balance"
        placeholder={lane === 'rows' && rowsTotal ? rowsTotal.toLocaleString('en-US') : undefined} />
        {lane === 'rows' && <small>Leave empty to use the total of the balances entered.</small>}</label>
    </Fields>

    {lane === 'file' && <>
      <input ref={picker} type="file" hidden accept=".csv,.xlsx" aria-label="Seller file" onChange={e => void readFile(e.target.files?.[0] ?? null)} />
      <Row style={{alignItems: 'center', marginBottom: 12}}>
        <Button type="button" disabled={busy || reading} onClick={() => picker.current?.click()}>{file ? `Change file (${file.name})` : "Choose the seller's file"}</Button>
        {reading && <span className="muted" role="status">Reading the column headings…</span>}
        {suggestion && <span className="muted" role="status">{keys.length - unmapped.length} of {keys.length} loan fields matched to the file's columns
          {suggestion.aiUsed ? ' · suggested with AI from the column headings only' : ''}</span>}
      </Row>
      {suggestion && <details open={unmapped.length > 0 || duplicated} style={{marginBottom: 14}}>
        <summary style={{cursor: 'pointer', fontSize: 13, color: INK.soft}}>{unmapped.length ? `Match the remaining ${count(unmapped.length, 'column')}` : 'Review how the columns were matched'}</summary>
        <Scroller><Grid style={{maxWidth: 720, marginTop: 8}}><thead><tr><th>Loan field</th><th>Column in the seller's file</th></tr></thead>
          <tbody>{keys.map(key => <tr key={key} data-attention={!mapping[key]}><td>{loanFieldLabel(key)}</td>
            <td><select aria-label={`Column for ${loanFieldLabel(key)}`} value={mapping[key] || ''} onChange={e => setMapping({...mapping, [key]: e.target.value})}>
              <option value="">Choose a column…</option>{suggestion.sourceColumns.map(c => <option key={c} value={c}>{c}</option>)}</select></td></tr>)}</tbody></Grid></Scroller>
      </details>}
      <GatedAction variant="primary" busy={busy} reason={fileReason} onClick={() => void receiveFile()}>Receive the offer</GatedAction>
    </>}

    {lane === 'rows' && <>
      <Notice>The agreement supplies the price{pricePer100 ? ` (${money(pricePer100)} per $100 of balance)` : ''}, currency (US dollars), rate type (fixed) and payment frequency (monthly),
        so they are not asked for each loan.</Notice>
      <label style={{marginBottom: 10}}>Paste from a spreadsheet (heading row first)
        <textarea value={paste} onChange={e => setPaste(e.target.value)} placeholder={ROW_FIELDS.map(f => loanFieldLabel(f.key)).join(', ')} /></label>
      <Row style={{marginBottom: 12}}>
        <SmallButton type="button" disabled={!paste.trim()} onClick={applyPaste}>Fill the table from the paste</SmallButton>
        <SmallButton type="button" onClick={() => setRows([...rows, {}])}>+ Add a loan</SmallButton>
        <span className="muted" style={{alignSelf: 'center'}}>{count(filled.length, 'loan')} · {money(rowsTotal)} balance at cutoff</span>
      </Row>
      <Scroller><Sheet><thead><tr><th>#</th>{ROW_FIELDS.map(f => <th key={f.key}>{loanFieldLabel(f.key)}{f.required ? '' : ' (optional)'}</th>)}<th></th></tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i}><td>{i + 1}</td>
          {ROW_FIELDS.map(f => <td key={f.key}>
            {f.type === 'amount' ? <AmountInput aria-label={`${loanFieldLabel(f.key)}, loan ${i + 1}`} placeholder="" value={r[f.key] ?? ''}
                onChange={v => setRows(rows.map((x, j) => j === i ? {...x, [f.key]: v} : x))} />
              : <input aria-label={`${loanFieldLabel(f.key)}, loan ${i + 1}`} style={{minWidth: f.width}} value={r[f.key] ?? ''}
                  inputMode={f.type === 'number' ? 'decimal' : undefined} placeholder={f.type === 'date' ? 'YYYY-MM-DD' : ''}
                  onChange={e => setRows(rows.map((x, j) => j === i ? {...x, [f.key]: e.target.value} : x))}
                  onBlur={e => f.type === 'date' && e.target.value ? setRows(rows.map((x, j) => j === i ? {...x, [f.key]: asIso(e.target.value)} : x)) : undefined} />}
          </td>)}
          <td><SmallButton type="button" disabled={rows.length === 1} aria-label={`Remove loan ${i + 1}`} onClick={() => setRows(rows.filter((_, j) => j !== i))}>Remove</SmallButton></td></tr>)}</tbody></Sheet></Scroller>
      {rowProblems.length > 1 && <Notice $tone="warn" style={{marginTop: 12}}><strong>{count(rowProblems.length, 'thing')} to fix:</strong>
        <ul>{rowProblems.slice(0, 8).map(p => <li key={p}>{p}</li>)}{rowProblems.length > 8 && <li>and {rowProblems.length - 8} more</li>}</ul></Notice>}
      <div style={{marginTop: 14}}><GatedAction variant="primary" busy={busy} reason={rowsReason} onClick={() => void receiveRows()}>Receive the offer</GatedAction></div>
    </>}
  </Section>;
}
